import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIcalCalendarSettings } from '@/features/integrations/api/ical-calendar/ical-calendar.hook';
import { ArrowLeft, CalendarDays, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { IcalCalendarSettings } from '../ical-calendar-settings';

interface CalendarIntegrationDialogProps {
  onClose: () => void;
  onGoogleCalendar: () => void;
  googleConnected: boolean;
}

/** Two choices, using the same large-row selector as the video template flow. */
export function CalendarIntegrationDialog({
  onClose,
  onGoogleCalendar,
  googleConnected,
}: CalendarIntegrationDialogProps) {
  const [view, setView] = useState<'choose' | 'ical' | 'google'>('choose');
  const { data, busy, disconnectMeet } = useIcalCalendarSettings();
  const title =
    view === 'ical'
      ? 'iCal'
      : view === 'google'
        ? 'Google Calendar + Meet'
        : 'Calendar';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader className="text-left">
          <div className="flex items-center gap-2 pr-6">
            {view !== 'choose' && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Calendar types"
                onClick={() => setView('choose')}
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription
            className={view === 'ical' ? 'sr-only' : undefined}
          >
            {view === 'choose'
              ? 'Choose your calendar type.'
              : view === 'ical'
                ? 'Share Borrah bookings and block time booked elsewhere.'
                : 'Manage your Google Calendar and online appointments.'}
          </DialogDescription>
        </DialogHeader>
        {view === 'choose' ? (
          <div className="flex flex-col gap-3">
            <button
              type="button"
              className="flex items-center gap-4 rounded-2xl border bg-background p-4 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:bg-accent"
              onClick={() => setView('ical')}
            >
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-muted">
                <CalendarDays className="size-6 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold">iCal</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Google Calendar, Apple Calendar, TreatWell and other calendar
                  RSS feeds.
                </p>
                {data?.enabled && (
                  <p className="mt-2 text-sm font-medium">Connected</p>
                )}
              </div>
              <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
            </button>
            <button
              type="button"
              className="flex items-center gap-4 rounded-2xl border bg-background p-4 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:bg-accent"
              onClick={() =>
                data?.meetAccount && !googleConnected
                  ? setView('google')
                  : onGoogleCalendar()
              }
            >
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-muted">
                <img
                  src="/google-calendar-icon.svg"
                  alt=""
                  className="size-6"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold">
                  Google Calendar + Meet
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Only supports Google Calendar. Required for online-only
                  services to create Google Meet links.
                </p>
                {googleConnected ? (
                  <p className="mt-2 text-sm font-medium">Connected</p>
                ) : data?.enabled ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Connecting Google Calendar turns off iCal sharing and
                    removes its imported blocks.
                  </p>
                ) : null}
              </div>
              <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
            </button>
          </div>
        ) : view === 'ical' ? (
          <IcalCalendarSettings />
        ) : (
          <div className="space-y-4">
            <p className="text-sm">
              Google Meet connected: {data?.meetAccount?.email}
            </p>
            <p className="text-sm text-muted-foreground">
              Your existing Meet connection stays active. Connect Google
              Calendar to sync bookings and busy time too.
            </p>
            <Button onClick={onGoogleCalendar}>Connect Google Calendar</Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() =>
                data?.meetAccount && disconnectMeet(data.meetAccount.id)
              }
            >
              Disconnect Google Meet
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
